/**
 * A small version of a photograph, made in the browser as it is uploaded. The
 * request of 5 October 2026 (third), section 6.
 *
 * The image viewer shows something at once and the full file when it has
 * arrived. The server keeps no image library and cannot make the small one, so
 * the browser does, as it already does for the app's icons, and sends it
 * beside the photograph as the part `thumbnail`.
 *
 * It is a convenience and never a condition: every way this can fail (a file
 * the browser cannot read, a browser that cannot draw, a result too big to be
 * worth sending) answers null, and the photograph goes up without one. The
 * server then shows the full file in its place, as it does for every
 * photograph stored before this.
 */

/** The longest edge of the small version, in pixels: a tile at three times its size. */
export const THUMBNAIL_EDGE = 480;

/** JPEG quality. A placeholder under a photograph, not a record of it. */
export const THUMBNAIL_QUALITY = 0.7;

/** What the server keeps: a JPEG of at most this many bytes. Twin of `PhotoRules.ThumbnailMaxBytes`. */
export const THUMBNAIL_MAX_BYTES = 512 * 1024;

export const THUMBNAIL_TYPE = 'image/jpeg';

/**
 * How long an upload waits for its small version before going without. A
 * photograph of twelve megapixels takes a moment to read on an old phone; one
 * the browser never finishes reading must not hold the upload for ever.
 */
export const THUMBNAIL_WAIT_MS = 4000;

export interface ThumbnailSize {
  width: number;
  height: number;
}

/**
 * The size of the small version of a picture: its own shape, its longest edge
 * brought down to `edge`. A picture already that small keeps its size, since
 * drawing it larger adds bytes and no detail. Null for a picture with no size.
 */
export function thumbnailSize(
  sourceWidth: number,
  sourceHeight: number,
  edge: number = THUMBNAIL_EDGE,
): ThumbnailSize | null {
  if (!(sourceWidth > 0) || !(sourceHeight > 0) || !(edge > 0)) return null;
  if (!Number.isFinite(sourceWidth) || !Number.isFinite(sourceHeight)) return null;

  const scale = Math.min(1, edge / Math.max(sourceWidth, sourceHeight));
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
}

function load(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const settle = (run: () => void) => {
      clearTimeout(waiting);
      URL.revokeObjectURL(url);
      run();
    };
    const waiting = setTimeout(
      () => settle(() => reject(new Error('That picture took too long to read.'))),
      THUMBNAIL_WAIT_MS,
    );
    image.onload = () => settle(() => resolve(image));
    image.onerror = () => settle(() => reject(new Error('That file could not be read as a picture.')));
    image.src = url;
  });
}

/**
 * The small JPEG of a photograph, or null when one cannot be made here.
 *
 * Drawn through an `<img>`, which turns the picture the way its orientation
 * says, as the full file is shown; the server keeps that orientation too, so
 * the two agree. Never throws.
 */
export async function makeThumbnail(file: Blob): Promise<Blob | null> {
  try {
    if (typeof document === 'undefined' || typeof Image === 'undefined') return null;

    // Asked first: where nothing can be drawn there is no reason to read the file.
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context === null) return null;

    const image = await load(file);
    const size = thumbnailSize(image.naturalWidth || image.width, image.naturalHeight || image.height);
    if (size === null) return null;

    canvas.width = size.width;
    canvas.height = size.height;
    // A JPEG has no transparency: a see-through PNG would come out black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size.width, size.height);
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, size.width, size.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, THUMBNAIL_TYPE, THUMBNAIL_QUALITY),
    );
    // A browser that cannot write a JPEG answers a PNG; the server would drop it.
    if (blob === null || blob.type !== THUMBNAIL_TYPE) return null;
    if (blob.size === 0 || blob.size > THUMBNAIL_MAX_BYTES) return null;
    return blob;
  } catch {
    return null;
  }
}
